/*@flow*/
import type { Node } from 'react';
import React, { createElement, Fragment } from 'react';
import * as t from 'lib/t';
import removeNils from 'lib/removeNils';
import cx from 'classnames';
import Breakpoints from '../util/Breakpoints';
import LabelComponent from './Label';
import ErrorComponent from './ErrorComponent';
import Help from './Help';
import FormGroup from './FormGroup';
import type { LocalsT, OptGroupT, SelectLocals } from '../types';
import VerticalCols from './VerticalCols';
import { SectionBefore, SectionAfter } from './Sections';

type PropsT = LocalsT & SelectLocals;

const SelectConfig = t.struct(
  {
    horizontal: t.maybe(Breakpoints),
    vertical: t.maybe(Breakpoints),
  },
  'SelectConfig'
);

const getConfig = (locals) => SelectConfig(locals.config || {});

const getAttrs = (locals) => {
  let attrs0 = locals.attrs;

  let className = cx(attrs0.className);
  className += (className ? ' ' : '') + 'form-control';

  let {
    isMultiple: multiple,
    disabled,
    value,
    onChange: onChange0,
    help,
  } = locals;

  let onChange = (evt) => {
    let value2 = evt.target.value;
    if (multiple) {
      value2 = Array.prototype.slice
        .call(evt.target.options)
        .filter((option) => option.selected)
        .map((option) => option.value);
    }

    onChange0(value2);
  };

  let describedBy;
  if (help) {
    const { id } = attrs0;
    describedBy = attrs0['aria-describedby'] || id + '-tip';
  }

  return removeNils({
    ...attrs0,
    multiple,
    disabled,
    value,
    className,
    help,
    'aria-describedby': describedBy,
    onChange,
  });
};

type Registry = {|
  Options: React$StatelessFunctionalComponent<*>,
  Select: React$StatelessFunctionalComponent<*>,
  Label: React$StatelessFunctionalComponent<*>,
  Help: React$StatelessFunctionalComponent<*>,
  Vertical: React$StatelessFunctionalComponent<*>,
  Horizontal: React$StatelessFunctionalComponent<*>,
  FormGroup: React$StatelessFunctionalComponent<*>,
|};

const clone = (overrides: $Shape<Registry> = {}) => {
  let registry = {};

  function Option(props: { disabled?: boolean, value: any, text: React$Node }) {
    return (
      <option disabled={props.disabled} value={props.value}>
        {props.text}
      </option>
    );
  }

  function OptGroup(props: any) {
    (props: OptGroupT); //eslint-disable-line
    return (
      <optgroup disabled={props.disabled} label={props.label}>
        {props.options.map((v, i) => (
          <Option {...v} key={i} />
        ))}
      </optgroup>
    );
  }

  function Options(locals: PropsT) {
    return locals.options.map((x, i) =>
      x.label
        ? createElement(OptGroup, { ...x, key: i })
        : createElement(Option, { ...x, key: i })
    );
  }

  function Select(locals: PropsT) {
    return (
      <select {...locals.attrs}>
        <registry.Options {...locals} />
      </select>
    );
  }

  function Label(locals) {
    const id = (locals.attrs: any).id;
    return createElement(LabelComponent, {
      label: locals.label,
      htmlFor: id,
      breakpoints: locals.config.horizontal,
      className: cx({ 'text-danger font-weight-bold': locals.hasError }),
      isRequired: locals.isRequired,
    });
  }

  function Vertical(locals) {
    return (
      <Fragment>
        <registry.Label {...locals} />
        <VerticalCols {...locals}>
          <registry.Select {...locals} />
          <ErrorComponent {...locals} />
          <registry.Help {...locals} />
        </VerticalCols>
      </Fragment>
    );
  }

  function Horizontal(locals) {
    const className = locals.label
      ? locals.config.horizontal.getInputClassName()
      : locals.config.horizontal.getOffsetClassName();

    return (
      <div className="form-row">
        <registry.Label {...locals} />
        <div className={cx(className)}>
          <registry.Select {...locals} />
          <ErrorComponent {...locals} />
          <registry.Help {...locals} />
        </div>
      </div>
    );
  }

  function SelectComponent(props: PropsT) {
    const config = getConfig(props);
    const attrs = getAttrs(props);

    const locals = { ...props, config, attrs };

    const children = config.horizontal ? (
      <registry.Horizontal {...locals} />
    ) : (
      <registry.Vertical {...locals} />
    );

    return (
      <Fragment>
        <registry.SectionBefore {...locals} />
        <registry.FormGroup {...locals}>{children}</registry.FormGroup>
        <registry.SectionAfter {...locals} />
      </Fragment>
    );
  }

  SelectComponent.clone = clone;

  registry = {
    Options,
    Select,
    Label,
    Help,
    Vertical,
    Horizontal,
    FormGroup,
    SectionBefore,
    SectionAfter,
    ...overrides,
  };

  return SelectComponent;
};

declare class CloneableClass {
  (locals: PropsT): Node;
  clone(overrides?: $Shape<Registry>): CloneableClass;
}
export default (clone(): CloneableClass);
